import { Fragment, useEffect, useState } from 'react';
import { Badge, Button, Card, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import {
    cargarDetallesVenta,
    limpiarDetallesVenta,
    listarDevolucionesVenta,
    generarPdfVenta
} from '../../actions/ventas';

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const formatMoney = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v);
};

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const buildUbicacionLabel = (mov) => {
    const parts = [];
    if (mov?.area?.nombre) parts.push(mov.area.nombre);
    if (mov?.organizador?.nombre) parts.push(mov.organizador.nombre);
    if (mov?.ubicacion?.nombre) parts.push(mov.ubicacion.nombre);
    return parts.join(' / ');
};

const renderBadgeStatus = (status) => (
    status
        ? <span className="badge bg-success">Activa</span>
        : <span className="badge bg-danger">Cancelada</span>
);

const DetalleVenta = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { detallesVenta: venta, loadingDetallesVenta, devolucionesVenta } = useSelector((state) => state.ventas);

    const [expandidos, setExpandidos] = useState({});

    const toggleExpandido = (key) => {
        setExpandidos((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    useEffect(() => {
        if (!id) return;

        dispatch(cargarDetallesVenta(id));
        dispatch(listarDevolucionesVenta(id));

        return () => {
            dispatch(limpiarDetallesVenta());
        };
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: false, nombre: 'Ventas', path: '/ventas' },
            { activo: true, nombre: `Venta ${venta?.folio || ''}`.trim(), path: `/ventas/detalle/${id}` }
        ]));
    }, [dispatch, id, venta]);

    if (loadingDetallesVenta || !venta) {
        return (
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Cargando venta...</h3>
                </div>
            </div>
        );
    }

    const productos = Array.isArray(venta.productos) ? venta.productos : [];
    const paquetes = Array.isArray(venta.paquetes) ? venta.paquetes : [];
    const esCotizacion = venta.tipo === 1;
    const prefMoneda = Number(venta.moneda) === 1 ? 'USD $' : '$';

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <div className="d-flex align-items-center justify-content-between mb-1 mt-5">
                        <div className="d-flex align-items-center gap-2">
                            <h3 className="mb-0">{esCotizacion ? 'Cotización' : 'Venta'} {venta?.folio || ''}</h3>
                            {renderBadgeStatus(venta?.status)}
                        </div>

                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-secondary"
                                onClick={() => dispatch(generarPdfVenta(id))}
                            >
                                <i className="fas fa-file-pdf" /> Generar PDF
                            </Button>

                            {venta?.status && (
                                <Button
                                    variant="warning"
                                    onClick={() => navigate(`/ventas/editar/${id}`)}
                                >
                                    <i className="fa fa-edit" /> Editar
                                </Button>
                            )}
                        </div>
                    </div>
                    <h6 className="text-muted mb-4">{venta?.cliente?.nombre || 'Sin cliente'}</h6>

                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="text-muted small">Fecha</div>
                            <div>{formatDate(venta?.fecha)}</div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-muted small">Método de pago</div>
                            <div>{venta?.metodo?.nombre || 'Sin definir'}</div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-muted small">Moneda</div>
                            <div>{Number(venta?.moneda) === 1 ? `USD (TC ${formatMoney(venta?.tipo_cambio)})` : 'MXN'}</div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-muted small">Total</div>
                            <div className="fw-bold">{prefMoneda} {formatMoney(venta?.total)}</div>
                        </div>
                    </div>

                    <Card className="mb-3">
                        <Card.Body>
                            <div className="fw-semibold mb-2">Productos</div>
                            <div className="table-responsive">
                                <Table bordered size="sm" className="mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 110 }}>Código INRA</th>
                                            <th>Producto</th>
                                            <th>Ubicación</th>
                                            <th className="text-center" style={{ width: 70 }}>IVA</th>
                                            <th className="text-center" style={{ width: 90 }}>Cantidad</th>
                                            <th style={{ width: 120 }}>Unidad</th>
                                            <th style={{ width: 150 }}>Precio</th>
                                            <th style={{ width: 150 }}>Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productos.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="text-muted text-center">Sin productos</td>
                                            </tr>
                                        ) : productos.map((p, idx) => {
                                            const cantidad = Number(p?.cantidad || 0);
                                            const precio = Number(p?.precio || 0);
                                            const subtotal = precio * cantidad * (p?.iva ? 1.16 : 1);

                                            return (
                                                <tr key={idx}>
                                                    <td>{p?.codigo_tienda || '-'}</td>
                                                    <td>{p?.nombre || 'Sin definir'}</td>
                                                    <td>{buildUbicacionLabel(p?.movimiento) || 'Sin existencia'}</td>
                                                    <td className="text-center">{p?.iva ? 'Sí' : 'No'}</td>
                                                    <td className="text-center">{formatNumber(cantidad)}</td>
                                                    <td>{p?.unidad?.nombre || '-'}</td>
                                                    <td>{prefMoneda} {formatMoney(precio)}</td>
                                                    <td className="fw-semibold">{prefMoneda} {formatMoney(subtotal)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {paquetes.length > 0 && (
                        <Card className="mb-3">
                            <Card.Body>
                                <div className="fw-semibold mb-2">Paquetes</div>
                                <div className="table-responsive">
                                    <Table bordered size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 30 }} />
                                                <th>Paquete</th>
                                                <th className="text-center" style={{ width: 70 }}>IVA</th>
                                                <th className="text-center" style={{ width: 100 }}>Cantidad</th>
                                                <th style={{ width: 150 }}>Precio</th>
                                                <th style={{ width: 150 }}>Subtotal</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paquetes.map((vp, idx) => {
                                                const key = vp?._id || `vp-${idx}`;
                                                const cantidad = Number(vp?.cantidad || 0);
                                                const precio = Number(vp?.precio || 0);
                                                const subtotal = precio * cantidad * (vp?.iva ? 1.16 : 1);
                                                const componentes = Array.isArray(vp?.componentes) ? vp.componentes : [];
                                                const abierto = !!expandidos[key];

                                                return (
                                                    <Fragment key={key}>
                                                        <tr>
                                                            <td className="text-center align-middle">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-link p-0"
                                                                    onClick={() => toggleExpandido(key)}
                                                                    title={abierto ? 'Ocultar productos del paquete' : 'Ver productos del paquete'}
                                                                >
                                                                    <i className={`fas fa-chevron-${abierto ? 'down' : 'right'}`} />
                                                                </button>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold">{vp?.paquete?.nombre || 'Sin definir'}</div>
                                                                {vp?.paquete?.codigo ? (
                                                                    <div className="text-muted" style={{ fontSize: 12 }}>{vp.paquete.codigo}</div>
                                                                ) : null}
                                                            </td>
                                                            <td className="text-center">{vp?.iva ? 'Sí' : 'No'}</td>
                                                            <td className="text-center">{formatNumber(cantidad)}</td>
                                                            <td>{prefMoneda} {formatMoney(precio)}</td>
                                                            <td className="fw-semibold">{prefMoneda} {formatMoney(subtotal)}</td>
                                                        </tr>
                                                        {abierto && (
                                                            <tr>
                                                                <td />
                                                                <td colSpan={5} className="p-0">
                                                                    <div className="table-responsive m-2">
                                                                        <Table bordered size="sm" className="mb-0" style={{ fontSize: 13 }}>
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Producto</th>
                                                                                    <th>Ubicación</th>
                                                                                    <th className="text-center" style={{ width: 90 }}>Cantidad</th>
                                                                                    <th style={{ width: 110 }}>Unidad</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {componentes.length === 0 ? (
                                                                                    <tr>
                                                                                        <td colSpan={4} className="text-muted text-center">Sin productos</td>
                                                                                    </tr>
                                                                                ) : componentes.map((c, cIdx) => {
                                                                                    const conversionComp = Number(c?.conversion || 1) || 1;
                                                                                    const cantidadTotalComp = (Number(c?.cantidad || 0) * cantidad) / conversionComp;

                                                                                    return (
                                                                                        <tr key={c?._id || cIdx}>
                                                                                            <td>
                                                                                                {c?.nombre || 'Sin definir'}
                                                                                                {c?.codigo_tienda ? (
                                                                                                    <span className="text-muted"> · {c.codigo_tienda}</span>
                                                                                                ) : null}
                                                                                            </td>
                                                                                            <td>{buildUbicacionLabel(c?.movimiento) || 'Sin existencia'}</td>
                                                                                            <td className="text-center">
                                                                                                {formatNumber(cantidadTotalComp)}
                                                                                            </td>
                                                                                            <td>{c?.unidad?.nombre || '-'}</td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </Table>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {Array.isArray(devolucionesVenta) && devolucionesVenta.length > 0 && (
                        <Card className="mb-3">
                            <Card.Body>
                                <div className="fw-semibold mb-2">Historial de devoluciones</div>
                                <div className="table-responsive">
                                    <Table bordered size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Producto</th>
                                                <th className="text-center">Cantidad</th>
                                                <th>Unidad</th>
                                                <th>Ubicación destino</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {devolucionesVenta.map((d) => (
                                                <tr key={d._id}>
                                                    <td>{formatDate(d?.fecha)}</td>
                                                    <td>
                                                        {d?.producto?.nombre || 'Sin definir'}
                                                        {d?.venta_paquete ? (
                                                            <span className="badge bg-secondary ms-2" style={{ fontSize: 10 }}>Paquete</span>
                                                        ) : null}
                                                    </td>
                                                    <td className="text-center">{formatNumber(d?.cantidad)}</td>
                                                    <td>{d?.unidad?.nombre || '-'}</td>
                                                    <td>{[d?.area?.nombre, d?.organizador?.nombre, d?.ubicacion?.nombre].filter(Boolean).join(' / ') || 'Sin ubicación'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {!venta?.status && (
                        <Badge bg="danger">Esta venta está cancelada</Badge>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

export default DetalleVenta;
